import EventTarget from 'goog:goog.events.EventTarget';
import Component from 'goog:goog.ui.Component';
import {
  html,
  render as litRender,
  RenderOptions,
  HTMLTemplateResult,
} from 'derivita/third_party/lit/packages/lit/src/html';

import { classMap } from 'derivita/third_party/lit/packages/lit/src/directives/class-map';
import { ifDefined } from 'derivita/third_party/lit/packages/lit/src/directives/if-defined';
import {
  isCompiledTemplateResult,
  isTemplateResult,
} from 'derivita/third_party/lit/packages/lit-html/src/directive-helpers';
import { removeChildren, safeHtmlToNode } from 'goog:goog.dom';
import SafeHtml from 'goog:goog.html.SafeHtml';
import { logException } from 'derivita/omaha/base/errorreporter';

export { html, HTMLTemplateResult as TemplateResult, classMap, ifDefined };

export abstract class EmbeddableComponent extends EventTarget {
  abstract getRootElement(): HTMLElement;
  abstract requestUpdate(): Promise<void>;
}

export function render(
  result: HTMLTemplateResult | EmbeddableComponent | Component,
  container: HTMLElement | DocumentFragment,
  options?: Partial<RenderOptions> | undefined
): void {
  let templateResult: HTMLTemplateResult;
  if (isTemplateResult(result)) {
    templateResult = result;
  } else {
    templateResult = html` ${result} `;
  }

  templateResult = html(
    templateResult.strings,
    ...templateResult.values.map((v) => normalizeTemplateValues(v))
  );

  try {
    litRender(templateResult, container, options);
  } catch (e) {
    // Log the exception so we can see what's going on.
    logException(e);
    // TODO: This is a dependency on lit internals. Figure out what error this is trying to catch and see if there's a better way to handle it.
    const owner = options?.renderBefore ?? container;
    options?.renderBefore || removeChildren(container);
    (owner as any)['_$litPart$'] = undefined;
    litRender(templateResult, container, options);
  }
}

/**
 * Converts template literal values into values that are
 * accepted by lit-html.
 * @param value value to normalize
 */
function normalizeTemplateValues(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((v) => normalizeTemplateValues(v));
  }

  if (value instanceof EmbeddableComponent) {
    return value.getRootElement();
  } else if (value instanceof Component) {
    return getClosureRoot(value);
  } else if (isTemplateResult(value)) {
    if (isCompiledTemplateResult(value)) {
      value.values = value.values.map((v) => normalizeTemplateValues(v));
      return value;
    }
    return html(
      value.strings,
      ...value.values.map((v) => normalizeTemplateValues(v))
    );
  } else if (value instanceof SafeHtml) {
    return safeHtmlToNode(value);
  }
  return value;
}

export function getClosureRoot(component: Component): HTMLElement {
  if (!component.getElement()) {
    component.createDom();
  }
  return component.getElementStrict() as HTMLElement;
}
